import React, { useState } from 'react';
import { resubmitPrescription, resubmitClaim } from '../../../Services/APIs/reimbursement';
import FormShell, { FormDone, focusFirstError, PRIMARY_BUTTON } from './FormShell';
import { UploadField, UploadSlots, useUploads } from './UploadField';
import StoredFile from './StoredFile';
import { UPLOAD_FIELD, fieldLabel, reuploadTarget, proofRounds, TONE } from './reimbursementStatus';
import { reuploadSlots, validateReupload, reuploadPayload, fieldDomId } from './formHelpers';

const FORM_ID = 'reupload';

// Re-uploads exactly the documents a reviewer asked for — nothing else on the Reimbursement can
// change here. After it is sent the Reimbursement goes back to review. A treatment-stage request
// is about the current proof, so its files are shown and replaced on that proof (by claim id).
const ReuploadForm = ({ reimbursement, onClose, onSuccess }) => {
    const request = reimbursement.document_request;
    const target = reuploadTarget(reimbursement);
    const isTreatment = target.stage === 'treatment';
    const slots = reuploadSlots(request.fields);
    const rounds = proofRounds(reimbursement);
    const round = isTreatment && rounds.length ? rounds[rounds.length - 1].round : 1;

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [isDone, setIsDone] = useState(false);
    const { uploads, bind } = useUploads({ type: isTreatment ? 'claim' : 'prescription' });

    const clearError = (slot) => setErrors((prev) => (prev[slot] ? { ...prev, [slot]: undefined } : prev));
    const upload = (slot) => {
        const props = bind(slot);
        return { ...props, onPick: (file) => { clearError(slot); props.onPick(file); } };
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const found = validateReupload(slots, uploads);
        setErrors(found);
        if (Object.keys(found).length) {
            focusFirstError(FORM_ID, found, slots.map(({ slot }) => slot));
            return;
        }
        setIsSubmitting(true);
        setFormError('');
        try {
            const payload = reuploadPayload({ requestFields: request.fields, uploads, isTreatment });
            if (isTreatment) await resubmitClaim(target.claimId, payload);
            else await resubmitPrescription(reimbursement._id, payload);
            setIsDone(true);
        } catch (err) {
            setFormError(err?.response?.data?.message || 'Failed to send. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isDone) {
        return (
            <FormDone
                testId="reupload-sent"
                title="Documents sent for review"
                body="We'll review them again and update your reimbursement here."
                onDone={onSuccess}
            />
        );
    }

    const footer = (
        <button type="submit" form={FORM_ID} disabled={isSubmitting} data-testid="reupload-submit" className={`${PRIMARY_BUTTON} w-full`}>
            {isSubmitting ? 'Sending…' : 'Send for review'}
        </button>
    );

    return (
        <FormShell
            title="Re-upload documents"
            subtitle={isTreatment ? (round > 1 ? `Proof #${round}` : 'Proof of treatment') : 'Reimbursement prescription'}
            onClose={onClose}
            error={formError}
            footer={footer}
            testId="reupload-form"
        >
            <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="space-y-4">
                <div className={`text-sm border rounded-xl p-3 ${TONE.action}`}>
                    <p className="font-semibold">What our reviewer asked for</p>
                    <p className="mt-1 text-portal-ink" data-testid="reupload-note">“{request.note}”</p>
                </div>

                {request.fields.map((field) => {
                    const stored = target.proof?.[field];
                    const storedList = Array.isArray(stored) ? stored : stored ? [stored] : [];
                    const multiple = Array.isArray(UPLOAD_FIELD[field]);
                    return (
                        <section key={field} className="border border-portal-border rounded-xl p-3 space-y-3 bg-portal-card">
                            <p className="text-sm font-bold text-portal-ink">{fieldLabel(field)}</p>
                            {storedList.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-portal-sub mb-1.5">Current file</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {storedList.map((url, index) => (
                                            <StoredFile
                                                key={url}
                                                variant="thumb"
                                                reimbursementId={reimbursement._id}
                                                claimId={isTreatment ? target.claimId : undefined}
                                                field={field}
                                                index={index}
                                                storedUrl={url}
                                                label={fieldLabel(field)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* An array field (X-rays, supporting documents) is replaced as a whole: at least one new file. */}
                            {multiple ? (
                                <UploadSlots
                                    slots={UPLOAD_FIELD[field]}
                                    uploads={uploads}
                                    bind={bind}
                                    labelFor={(index) => `New file ${index + 1}${index === 0 ? '' : ' (optional)'}`}
                                    addLabel="Add another file"
                                    idFor={(slot) => fieldDomId(FORM_ID, slot)}
                                    testIdFor={(slot) => `reupload-${slot}`}
                                    errors={errors}
                                    requiredFirst
                                    onPick={clearError}
                                />
                            ) : slots.filter((s) => s.field === field).map(({ slot, required }) => (
                                <UploadField
                                    key={slot}
                                    id={fieldDomId(FORM_ID, slot)}
                                    label="New file"
                                    required={required}
                                    testId={`reupload-${slot}`}
                                    error={errors[slot]}
                                    {...upload(slot)}
                                />
                            ))}
                        </section>
                    );
                })}
            </form>
        </FormShell>
    );
};

export default ReuploadForm;
